<template>
	<ValidationForm>
		<Modal width="650px">
			<template #modal-title>
				{{ serviceName }}
			</template>

			<template #modal-content>
				<div class="cassie-vertical-md">
					<p>
						Configure the associations between the service and MSPA fields.<br>
						(An MSPA field can only be linked to
						<strong>
							one
						</strong> service in this collection template.)
					</p>

					<v-row dense>
						<v-col cols="6">
							<Dropdown
								v-model="selectedElementIds"
								:search-input.sync="searchInput"
								multiple
								select-all
								searchable
								chips
								label="MSPA Fields"
								:items="availableElementItems"
								@change="updateSelectAllChecked"
							>
								<template
									v-if="showSelectAll"
									#prepend-item
								>
									<v-list-item
										dense
										@click="toggleSelectAll"
									>
										<v-checkbox
											v-model="selectAllChecked"
											dense
											:disabled="!availableElementItems.length"
											@click="toggleSelectAll"
										/>
										<v-list-item-title class="pb-2">
											Select All
										</v-list-item-title>
									</v-list-item>
									<v-divider />
								</template>
							</Dropdown>
						</v-col>

						<v-col
							cols="3"
							class="d-flex align-end"
						>
							<PrimaryActionButton
								:disabled="!selectedElementIds.length"
								@click="addElements"
							>
								Add
							</PrimaryActionButton>
						</v-col>
					</v-row>

					<DataTable
						:headers="tableHeaders"
						:items="chosenElements"
						dense
						hide-default-footer
						class="mt-4"
					>
						<template #item.elementName="{ item }">
							{{ item.elementName }}
						</template>
						<template
							v-if="userFullPermissions"
							#item.action="{ item }"
						>
							<v-menu offset-y>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										@click="removeElement(item.id)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Remove
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</div>
			</template>

			<template #modal-footer>
				<SecondaryActionButton @click="$emit('close')">
					Cancel
				</SecondaryActionButton>
				<v-spacer />
				<PrimaryActionButton
					v-if="userFullPermissions"
					@click="confirmChanges"
				>
					Confirm
				</PrimaryActionButton>
			</template>
		</Modal>
	</ValidationForm>
</template>

<script>
import Modal from '../../../../../../../shared/components/modal.vue'
import ValidationForm from '../../../../../../../shared/components/validation-form.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../../shared/components/secondary-action-button.vue'
import { MSPA_ELEMENT_ITEMS, getMspaLabel } from '../../../../../enums/mspa-elements.js'

export default {
	name: 'cookie-mspa-script-modal',
	components: {
		Modal,
		ValidationForm,
		Dropdown,
		DataTable,
		PrimaryActionButton,
		SecondaryActionButton
	},
	props: {
		service: { type: Object, required: true },
		takenBy: { type: Object, default: () => ({}) },
		userFullPermissions: Boolean
	},
	data () {
		return {
			selectedElementIds: [],
			selectAllChecked: false,
			searchInput: null,
			localService: {
				...this.service,
				mspaElementIds: Array.isArray(this.service.mspaElementIds)
					? [...this.service.mspaElementIds]
					: []
			}
		}
	},
	computed: {
		serviceName () {
			const { cookieScriptName, brandName } = this.localService
			return brandName ? `${cookieScriptName} – ${brandName}` : cookieScriptName
		},
		availableElementItems () {
			return MSPA_ELEMENT_ITEMS
				.filter(i => !this.localService.mspaElementIds.includes(i.value))
				.filter(i => {
					const taken = this.takenBy[i.value]
					return !taken || taken.scriptId === this.localService.id
				})
		},
		showSelectAll () {
			return this.availableElementItems.length > 1
		},
		chosenElements () {
			return this.localService.mspaElementIds.map(id => ({
				id,
				elementName: getMspaLabel(id)
			}))
		},
		tableHeaders () {
			return [
				{ text: 'MSPA Field', value: 'elementName', width: '100%' },
				{ text: 'Action', value: 'action', width: '80px', align: 'center', sortable: false }
			]
		}
	},
	watch: {
		availableElementItems () {
			const valid = this.availableElementItems.map(i => i.value)
			this.selectedElementIds = this.selectedElementIds.filter(id => valid.includes(id))
		}
	},
	methods: {
		addElements () {
			this.selectedElementIds.forEach(val => {
				if (!this.localService.mspaElementIds.includes(val)) {
					this.localService.mspaElementIds.push(val)
				}
			})
			this.selectedElementIds = []
			this.searchInput = null
		},
		removeElement (id) {
			const idx = this.localService.mspaElementIds.indexOf(id)
			if (idx !== -1) this.localService.mspaElementIds.splice(idx, 1)
		},
		confirmChanges () {
			this.$emit('save', {
				serviceId: this.localService.id,
				mspaElementIds: [...this.localService.mspaElementIds]
			})
			this.$emit('close')
		},
		toggleSelectAll () {
			if (this.selectAllChecked) {
				this.selectAllChecked = false
				this.selectedElementIds = []
			} else {
				this.selectAllChecked = true

				const filtered = this.searchInput
					? this.availableElementItems.filter(i =>
						i.text.toLowerCase().includes(this.searchInput.toLowerCase()))
					: this.availableElementItems
				this.selectedElementIds = filtered.map(i => i.value)
			}
		},
		updateSelectAllChecked () {
			const filtered = this.searchInput
				? this.availableElementItems.filter(i =>
					i.text.toLowerCase().includes(this.searchInput.toLowerCase()))
				: this.availableElementItems
			this.selectAllChecked = (this.selectedElementIds.length === filtered.length)
		}
	}
}
</script>
